import React, { useState } from 'react';
import { ArrowRight, Calculator } from 'lucide-react';

const BUSINESS_TYPES = [
  { slug: 'service', name: 'Service Business', avgDealValue: 3000, leadToAppt: 0.35, apptToClose: 0.40 },
  { slug: 'agency', name: 'Agency / Consultancy', avgDealValue: 5000, leadToAppt: 0.30, apptToClose: 0.35 },
  { slug: 'ecommerce', name: 'E-Commerce', avgDealValue: 150, leadToAppt: 0.25, apptToClose: 0.50 },
  { slug: 'saas', name: 'SaaS / Tech', avgDealValue: 2000, leadToAppt: 0.20, apptToClose: 0.30 },
  { slug: 'healthcare', name: 'Healthcare / Wellness', avgDealValue: 2500, leadToAppt: 0.30, apptToClose: 0.45 },
  { slug: 'realestate', name: 'Real Estate', avgDealValue: 8000, leadToAppt: 0.25, apptToClose: 0.20 },
  { slug: 'education', name: 'Education / Coaching', avgDealValue: 3000, leadToAppt: 0.30, apptToClose: 0.35 },
  { slug: 'other', name: 'Other', avgDealValue: 2000, leadToAppt: 0.30, apptToClose: 0.35 },
];

const MiniROICalculator = () => {
  const [selectedSlug, setSelectedSlug] = useState('');
  const [avgDealValue, setAvgDealValue] = useState('');
  const [desiredDeals, setDesiredDeals] = useState('');
  const [result, setResult] = useState(null);

  const selectedBiz = BUSINESS_TYPES.find(t => t.slug === selectedSlug);

  const handleBizChange = (slug) => {
    setSelectedSlug(slug);
    const biz = BUSINESS_TYPES.find(t => t.slug === slug);
    if (biz) setAvgDealValue(biz.avgDealValue.toString());
    setResult(null);
  };

  const calculate = () => {
    const dealVal = parseFloat(avgDealValue) || 0;
    const deals = parseFloat(desiredDeals) || 0;
    if (dealVal <= 0 || deals <= 0 || !selectedBiz) return;

    const leadsNeeded = Math.ceil(deals / selectedBiz.apptToClose / selectedBiz.leadToAppt);
    const monthlyRevenue = deals * dealVal;

    setResult({ leadsNeeded, monthlyRevenue });
  };

  const formatCurrency = (num) => {
    if (num >= 1000000) return `$${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `$${Math.round(num / 1000)}K`;
    return `$${num.toLocaleString()}`;
  };

  return (
    <div className="mini-roi" data-testid="mini-roi-calculator">
      <div className="mini-roi__header">
        <Calculator size={20} />
        <h3>Quick ROI Calculator</h3>
      </div>

      <div className="mini-roi__form">
        <div className="mini-roi__field">
          <label className="mini-roi__label">Your business type</label>
          <select
            value={selectedSlug}
            onChange={(e) => handleBizChange(e.target.value)}
            className="mini-roi__select"
            data-testid="mini-roi-trade-select"
          >
            <option value="">Select your business</option>
            {BUSINESS_TYPES.map((t) => (
              <option key={t.slug} value={t.slug}>{t.name}</option>
            ))}
          </select>
        </div>

        <div className="mini-roi__field">
          <label className="mini-roi__label">Average deal value</label>
          <div className="mini-roi__input-wrap">
            <span className="mini-roi__prefix">$</span>
            <input
              type="number"
              value={avgDealValue}
              onChange={(e) => { setAvgDealValue(e.target.value); setResult(null); }}
              placeholder="e.g. 3000"
              data-testid="mini-roi-job-value"
            />
          </div>
        </div>

        <div className="mini-roi__field">
          <label className="mini-roi__label">Desired deals per month</label>
          <input
            type="number"
            value={desiredDeals}
            onChange={(e) => { setDesiredDeals(e.target.value); setResult(null); }}
            placeholder="e.g. 15"
            className="mini-roi__input"
            data-testid="mini-roi-jobs"
          />
        </div>

        <button
          className="mini-roi__btn"
          onClick={calculate}
          disabled={!selectedSlug || !avgDealValue || !desiredDeals}
          data-testid="mini-roi-calculate-btn"
        >
          Calculate My ROI <ArrowRight size={14} />
        </button>
      </div>

      {result && (
        <div className="mini-roi__result" data-testid="mini-roi-result">
          <div className="mini-roi__result-row">
            <span>Leads needed</span>
            <strong>{result.leadsNeeded} / mo</strong>
          </div>
          <div className="mini-roi__result-row mini-roi__result-row--big">
            <span>Projected revenue</span>
            <strong className="mini-roi__revenue">
              {formatCurrency(result.monthlyRevenue)}<span>/mo</span>
            </strong>
          </div>
        </div>
      )}
    </div>
  );
};

export default MiniROICalculator;
